import { useMemo } from 'react';
import { useIsOrgAdmin } from '@jira/platform__services-is-org-admin/src/is-org-admin.tsx';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import {
	verticalInstantiationResource,
	type VerticalInstantiationsResult,
} from '@jira/router-resources__itam/src/verticalInstantiationResource.tsx';
import { workspaceResource } from '@jira/servicedesk__insight-common-services-workspace-context/src/workspaceResource.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { HAM_ADMIN, HAM_USER, type UserRole } from './UserRole.tsx';
import type {
	HamVerticalInstantiation,
	ItamTenantContextResult,
} from './ItamTenantContextTypes.tsx';
import { ADMINISTER_HAM, VIEW_HAM, type HamPermissions } from './HamPermissions.tsx';

function deriveRole(isAdmin: boolean): UserRole {
	return isAdmin ? HAM_ADMIN : HAM_USER;
}

function getHamVerticalInstantiation(
	verticalInstantiations: VerticalInstantiationsResult | undefined,
): HamVerticalInstantiation | null {
	const hamNode = verticalInstantiations?.edges.find((edge) => {
		const node = edge.node;
		return node?.type === 'HAM' && node.id !== '';
	})?.node;
	if (!hamNode || hamNode.type !== 'HAM' || hamNode.id === '') {
		return null;
	}

	const categories = (hamNode.categories?.edges ?? []).reduce<
		Array<{ readonly id: string; readonly category: string }>
	>((accumulator, categoryEdge) => {
		const categoryNode = categoryEdge.node;
		if (categoryNode?.id && categoryNode.category) {
			accumulator.push(categoryNode);
		}
		return accumulator;
	}, []);

	return {
		id: hamNode.id,
		status: hamNode.status,
		categories,
	};
}

/**
 * Hook that exposes JSM permissions, required license (e.g. JSM Premium for HAM; can support Jira later),
 * user role (Jira admin / user), and derived access for ITAM pages.
 * Access is granted only when user has the required app permission and tenant has the required license.
 * Role is derived from useIsAdmin (Jira admin); site-admin is deprecated and not exposed.
 */
export function useItamTenantContextMain(): ItamTenantContextResult {
	const appPermissions = useApplicationPermissions();
	const appEditions = useAppEditions();
	const isAdmin = useIsAdmin();
	const { isOrgAdmin, loading: isLoadingOrgAdmin } = useIsOrgAdmin();
	const { data: workspaceId } = useResource(workspaceResource);
	const { data: verticalInstantiations } = useResource(verticalInstantiationResource);
	const cloudId = useCloudId();

	return useMemo(() => {
		const isLoading = appPermissions == null || appEditions == null || isLoadingOrgAdmin;

		// must have premium edition and service desk access
		const hasRequiredLicense = appEditions?.serviceDesk === PREMIUM_EDITION;
		const hasServiceDeskAccess = !!appPermissions?.hasServiceDeskAccess;

		const hasAccess = !isLoading && hasServiceDeskAccess && hasRequiredLicense;

		const role = deriveRole(isAdmin);
		const isUser = hasAccess && !isAdmin;

		const permissions: HamPermissions = {
			[ADMINISTER_HAM]: isAdmin,
			[VIEW_HAM]: hasAccess,
		};
		const shouldShowItam = !isLoading && hasAccess;
		const hamVerticalInstantiation = getHamVerticalInstantiation(verticalInstantiations);

		return {
			ham: {
				hasRequiredLicense,
				hasAccess,
				isUser,
				isAdmin,
				role,
				isLoading,
				permissions,
				shouldShowItam,
				verticalInstantiation: hamVerticalInstantiation,
			},
			isOrgAdmin,
			isLoading: isLoadingOrgAdmin,
			workspaceId,
			cloudId,
			// TODO : we will remove this once we integarte the ham.verticalInstantiation.
			verticalInstantiationId: hamVerticalInstantiation?.id ?? null,
			verticalInstantiationStatus: hamVerticalInstantiation?.status ?? null,
		};
	}, [
		appPermissions,
		appEditions,
		isAdmin,
		isOrgAdmin,
		isLoadingOrgAdmin,
		verticalInstantiations,
		workspaceId,
		cloudId,
	]);
}

const useItamTenantContextDefault = (): ItamTenantContextResult => ({
	ham: {
		shouldShowItam: false,
		hasRequiredLicense: false,
		hasAccess: false,
		isUser: false,
		isAdmin: false,
		role: HAM_ADMIN,
		isLoading: false,
		permissions: { VIEW_HAM: false, ADMINISTER_HAM: false },
		verticalInstantiation: null,
	},
	isLoading: false,
	isOrgAdmin: false,
	workspaceId: null,
	cloudId: null,
	verticalInstantiationId: null,
	verticalInstantiationStatus: null,
});

export const useItamTenantContext: () => ItamTenantContextResult = functionWithCondition(
	() => fg('itam_ftw'),
	useItamTenantContextMain,
	// Returns default values just to satisfy the hooks `readonly` types.
	useItamTenantContextDefault,
);
